import { useCallback, useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import type { ReactTable, Row, Table } from '@/lib/tanstack-table';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { BuyingOrderLineType } from '../utils/buyingOrdersUrl';
import { groupLines, type GroupedLines } from '../utils/lineGroups';
import {
  buildNeedRows,
  withNeedStockLines,
  sectionOf,
  type NeedRows,
} from '../utils/needRows';
import type { NeedRowsValue } from './needRowsContext';

/** Group headers show only while the rows are unsorted (in group order). */
export function isLineGroupingOn(table: Table<AssignmentLine>): boolean {
  return table.store.state.sorting.length === 0;
}

interface LineGroupTable {
  grouped: GroupedLines;
  /** Table rows: need rows with their SO lines, or the flat lines when sorted. */
  rows: AssignmentLine[];
  /** For NeedRowsContext; null while sorted (no need rows then). */
  needRows: NeedRowsValue | null;
  /** Pass to DataTable; both are undefined while the buyer has re-sorted. */
  groupProps: (table: ReactTable<AssignmentLine>) => {
    groupHeaderFn?: (row: Row<AssignmentLine>, index: number) => ReactNode;
    isRowHidden?: (row: Row<AssignmentLine>) => boolean;
  };
}

/**
 * Detail-pane grouping: lines of the page's allowed types, ordered back orders
 * -> product on the fly -> stock, with a collapsible 24px header row per group.
 * Headers only make sense while the rows are in group order, so an explicit
 * column sort turns grouping off and shows the flat sorted list.
 */
export function useLineGroupTable(
  lines: readonly AssignmentLine[],
  allowed: readonly BuyingOrderLineType[],
  {
    sorted,
    selectedLineId,
  }: { sorted: boolean; selectedLineId: string | undefined },
): LineGroupTable {
  const gt = useGT();
  // GEN-2938: an item x location's SO lines fold under one need row while the
  // rows are in group order; a column sort shows the flat lines, as for the
  // section headers. A folded need keeps its stock lines even with Stock
  // filtered out, and they count in the table's lines (footer totals too).
  const typed = groupLines(lines, allowed);
  const grouped = sorted
    ? typed
    : { ...typed, lines: withNeedStockLines(lines, typed.lines) };
  const needs: NeedRows | null = sorted ? null : buildNeedRows(grouped.lines);
  const expansion = useNeedExpansion(needs, selectedLineId);
  const [collapsed, setCollapsed] = useState<ReadonlySet<BuyingOrderLineType>>(
    () => new Set(),
  );
  const toggle = useCallback((type: BuyingOrderLineType) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const labels: Record<BuyingOrderLineType, string> = {
    back_order: gt('Back orders', { context: 'detail line group header' }),
    potf: gt('POTF', {
      context: 'detail line group header, product on the fly',
    }),
    stock: gt('Stock', { context: 'detail line group header' }),
  };

  // `3 items · 162 lines` once a section folds lines under needs.
  const countLabel = (type: BuyingOrderLineType): string => {
    const lineCount = needs?.lineCounts[type] ?? grouped.counts[type];
    const items = needs?.itemCounts[type] ?? lineCount;
    if (items === lineCount) return String(lineCount);
    const context = 'detail line group header, item needs then their lines';
    return items === 1
      ? gt('1 item · {lines} lines', { lines: lineCount, context })
      : gt('{items} items · {lines} lines', {
          items,
          lines: lineCount,
          context,
        });
  };

  const groupProps: LineGroupTable['groupProps'] = (table) => {
    if (!isLineGroupingOn(table)) return {};
    return {
      groupHeaderFn: (row, index) => {
        const type = sectionOf(row.original, needs);
        const previous = table.getRowModel().rows[index - 1];
        if (previous && sectionOf(previous.original, needs) === type) {
          return null;
        }
        const open = !collapsed.has(type);
        return (
          <button
            type="button"
            onClick={() => toggle(type)}
            aria-expanded={open}
            className="flex h-6 w-full items-center gap-1.5 text-left text-[11px] font-medium"
          >
            {open ? (
              <CaretDownIcon className="h-3 w-3" aria-hidden />
            ) : (
              <CaretRightIcon className="h-3 w-3" aria-hidden />
            )}
            <span>{labels[type]}</span>
            <span className="tabular-nums text-muted-foreground">
              {countLabel(type)}
            </span>
          </button>
        );
      },
      isRowHidden: (row) =>
        collapsed.has(sectionOf(row.original, needs)) ||
        expansion.isFolded(row.original.suggestionId),
    };
  };

  return {
    grouped,
    rows: needs?.rows ?? grouped.lines,
    needRows: expansion.value,
    groupProps,
  };
}

/**
 * Which need rows are open. Collapsed by default; a selected SO line (a deep
 * link, a search hit, ↑/↓) opens its need.
 */
function useNeedExpansion(
  needs: NeedRows | null,
  selectedLineId: string | undefined,
) {
  const selectedNeed =
    selectedLineId == null ? undefined : needs?.needOf.get(selectedLineId);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() =>
    selectedNeed == null ? new Set() : new Set([selectedNeed]),
  );
  // Keyed on the line and its need, so a new selection inside a collapsed
  // need opens it, and so does the same line once its need's rows load.
  const selection = `${selectedLineId ?? ''}|${selectedNeed ?? ''}`;
  const [openedFor, setOpenedFor] = useState(selection);
  if (selection !== openedFor) {
    setOpenedFor(selection);
    if (selectedNeed != null && !expanded.has(selectedNeed)) {
      setExpanded(new Set(expanded).add(selectedNeed));
    }
  }
  // React Compiler memoizes these; no manual useCallback/useMemo.
  const toggle = (needId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(needId)) next.delete(needId);
      else next.add(needId);
      return next;
    });
  };
  const value: NeedRowsValue | null =
    needs == null || needs.needs.size === 0
      ? null
      : {
          needs: needs.needs,
          needOf: needs.needOf,
          isExpanded: (needId) => expanded.has(needId),
          toggle,
        };
  const isFolded = (suggestionId: string): boolean => {
    const needId = needs?.needOf.get(suggestionId);
    return needId != null && !expanded.has(needId);
  };
  return { value, isFolded };
}
